<template>
  <div class="bg-image-component" :class="{ 'overflow-hidden': !overflow }">
    <div v-if="loader" class="img-loader-wrapper" :style="{ background: loader.bg }">
      <div class="img-loader" :style="{
        borderColor: loader.color,
        borderLeftColor: loader.bg,
        width: loader.size + 'px',
        height: loader.size + 'px',
      }"></div>
    </div>

    <div ref="bg-image-wrapper" class="bg-image-wrapper" :class="{
      loaded: loaded,
      blur: effect ? effect.type === 'blur' : false,
      height: effect ? effect.type === 'height' : false,
      'show-placeholder': showPlaceholder,
    }" :style="{ backgroundColor: bgColor }">
      <img v-if="lazyLoad" ref="image" :fake-src="src" @load="imageLoadHandler()" style="display: none" />
      <img v-else ref="image" :src="src" @load="imageLoadHandler()" style="display: none" />

      <div v-if="showPlaceholder" ref="bg-placeholder" class="bg-image bg-image-low" :style="[
        {
          backgroundImage: `url(${placeholder})`,
          opacity: opacity,
        },
        filter
          ? {
            backgroundColor: filter.color,
            backgroundBlendMode: filter.type,
          }
          : {},
      ]"></div>

      <div ref="bg-image" class="bg-image bg-image-high" :style="[
        {
          opacity: opacity,
        },
        filter
          ? {
            backgroundColor: filter.color,
            backgroundBlendMode: filter.type,
          }
          : {},
      ]"></div>

      <div v-if="overlay" class="overlay" :style="{ backgroundColor: overlay }"></div>

      <div v-if="pattern" class="pattern" :style="{ backgroundImage: `url(${pattern})` }"></div>
    </div>

    <slot></slot>
  </div>
</template>

<script>
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default {
  props: {
    src: {
      type: String,
      required: true,
      default: null,
    },
    placeholder: {
      type: String,
      required: false,
    },
    loader: {
      type: Object,
      required: false,
      default: null,
    },
    bgColor: {
      type: String,
      required: false,
      default: null,
    },
    effect: {
      type: Object,
      required: false,
      default: null,
    },
    parallax: {
      type: String,
      required: false,
      default: null,
    },
    overflow: {
      type: Boolean,
      required: false,
      default: true,
    },
    debug: {
      type: Boolean,
      required: false,
      default: false,
    },
    opacity: {
      type: Number,
      required: false,
      default: null,
    },
    overlay: {
      type: String,
      required: false,
      default: null,
    },
    filter: {
      type: Object,
      required: false,
      default: null,
    },
    pattern: {
      type: String,
      required: false,
      default: null,
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      observer: null,
      effectObserver: null,
      effectObserverTriggered: false,
      loaded: false,
    };
  },
  computed: {
    showPlaceholder() {
      if (this.placeholder) {
        return !this.imgIsCached();
      } else {
        return false;
      }
    },
  },
  methods: {
    imgIsCached() {
      return false;
      // var image = new Image();
      // image.src = this.src;
      // return image.complete;
    },
    imageLoadHandler() {
      this.$emit("loaded");

      if (this.lazyLoad) {
        this.$refs["bg-image"].style.backgroundImage = `url(${this.$refs[
          "image"
        ].getAttribute("fake-src")})`;
      } else {
        this.$refs["bg-image"].style.backgroundImage = `url(${this.$refs[
          "image"
        ].getAttribute("src")})`;
      }

      if (
        (this.effect && this.effectObserverTriggered) ||
        (this.effect && this.effect.isHero)
      ) {
        setTimeout(() => {
          if (this.effect.type === "height") {
            this.$refs["bg-image-wrapper"].classList.add("animation-active");
          } else {
            this.$refs["bg-image-wrapper"].classList.add(this.effect.type);
          }
        }, 0);
      }

      if (!this.debug) {
        this.loaded = true;
      }
    },
    loadHighResImage(entries) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return;
        }

        this.$refs["image"].setAttribute(
          "src",
          this.$refs["image"].getAttribute("fake-src")
        );

        this.observer.unobserve(target);
      });
    },
    runEffect(entries) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return;
        }

        // if (this.loaded) {
        if (this.effect.type === "zoom-in") {
          this.$refs["bg-image-wrapper"].classList.add(this.effect.type);
        } else if (this.effect.type === "height") {
          this.$refs["bg-image-wrapper"].classList.add("animation-active");
        }

        this.effectObserverTriggered = true;

        this.effectObserver.unobserve(target);
        // }
      });
    },
  },
  created() {
    if (this.lazyLoad) {
      this.observer = new IntersectionObserver(this.loadHighResImage, {
        root: this.$el,
        threshold: 0,
      });
    }

    if (this.effect) {
      this.effectObserver = new IntersectionObserver(this.runEffect, {
        root: this.$el,
        threshold: this.effect.threshold ? this.effect.threshold : 1,
      });
    }
  },
  mounted() {
    if (this.lazyLoad) {
      this.observer.observe(this.$el);
    }

    if (this.effect) {
      this.effectObserver.observe(this.$el);
    }

    if (this.parallax) {
      const bgHigh = this.$el.querySelector(".bg-image-high");
      let backgroundTo;

      if (this.parallax === "up") {
        bgHigh.style.backgroundPosition = `50% 50%`;
        backgroundTo = `50% 100%`;
      } else if (this.parallax === "down") {
        bgHigh.style.backgroundPosition = `50% 50%`;
        backgroundTo = `50% 0%`;
      }

      gsap.to(bgHigh, {
        backgroundPosition: backgroundTo,
        ease: "none",
        scrollTrigger: {
          trigger: bgHigh,
          scrub: 1,
          start: "top top",
          end: "bottom top",
          markers: false,
        },
      });

      // gsap.to(this.$el.children[0].children[2], {
      //     backgroundPosition: `50% ${innerHeight / 2}px`,
      //     ease: "none",
      //     scrollTrigger: {
      //         // trigger: section,
      //         scrub: true
      //     }
      // });

      // gsap.to(this.$el.children[0].children[2], {
      //     scrollTrigger: {
      //         scrub: true
      //     },
      //     y: (i, target) => -ScrollTrigger.maxScroll(window) * target.dataset.speed,
      //     ease: "none"
      // })
    }
  },
  beforeDestroy() {
    if (this.lazyLoad) {
      this.observer.disconnect();
    }

    if (this.effect) {
      this.effectObserver.disconnect();
    }
  },
};
</script>

<style lang="scss">
bg-image {
  display: block;
  position: relative;
}

.bg-image-component {
  position: relative;

  .img-loader-wrapper {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    .img-loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) translateZ(0);
      border-radius: 50%;
      width: 60px;
      height: 60px;
      border-width: 2px;
      border-style: solid;
      border-left: 2px solid;
      animation: load 1.1s infinite linear;

      &::after {
        border-radius: 50%;
        width: 10em;
        height: 10em;
      }
    }
  }

  .bg-image-wrapper {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: hidden;

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 100%;
    }

    .pattern {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 100%;
    }

    .bg-image {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0%;
      left: 0%;
      background-position: 50% 50%;
      background-size: cover;
      background-repeat: no-repeat;
      backface-visibility: hidden;
      image-rendering: -webkit-optimize-contrast;

      &.bg-image-low {
        opacity: 1;
        transition: opacity 500ms 500ms ease-out;
      }

      &.bg-image-high {
        opacity: 0;
        transition: opacity 500ms ease-out;
      }
    }

    &.blur {
      &.show-placeholder {
        .bg-image {
          &.bg-image-low {
            filter: blur(20px);
            transition: opacity 500ms 500ms ease-out, filter 1000ms ease-out;
          }

          &.bg-image-high {
            filter: blur(20px);
            transition: opacity 500ms ease-out, filter 1000ms ease-out;
          }
        }
      }
    }

    &.zoom-in {
      .bg-image {

        &.bg-image-low,
        &.bg-image-high {
          transform: scale(1);
          transition: opacity 500ms ease-out, filter 1000ms ease-out,
            transform 7000ms ease-out;
        }
      }
    }

    &.height {
      &.show-placeholder {
        .bg-image {
          clip-path: polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%);

          &.bg-image-low,
          &.bg-image-high {
            transition: opacity 500ms ease-out, filter 1000ms ease-out,
              clip-path 1000ms ease-out;
          }
        }
      }
    }

    &.loaded {
      .bg-image {
        &.bg-image-low {
          opacity: 0;
        }

        &.bg-image-high {
          opacity: 1;
        }
      }

      &.blur {
        .bg-image {
          &.bg-image-high {
            filter: blur(0px);
          }
        }

        &.show-placeholder {
          .bg-image {
            &.bg-image-low {
              filter: blur(0px);
            }
          }
        }
      }

      &.zoom-in {
        .bg-image {

          &.bg-image-low,
          &.bg-image-high {
            transform: scale(1.05);
          }
        }
      }

      &.animation-active {
        &.height {
          .bg-image {
            clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
          }
        }
      }
    }
  }

  &.bg-contain {
    .bg-image-wrapper {
      .bg-image {
        background-size: contain;
      }
    }
  }

  &.bg-opacity-30 {
    .bg-image-wrapper {
      &.loaded {
        .bg-image {
          &.bg-image-high {
            opacity: 0.3;
          }
        }
      }
    }
  }

  &.bg-left {
    .bg-image-wrapper {
      .bg-image {
        background-position: 0% 0%;
      }
    }
  }

  &.bg-right {
    .bg-image-wrapper {
      .bg-image {
        background-position: 100% 50%;
      }
    }
  }

  &.bg-top {
    .bg-image-wrapper {
      .bg-image {
        background-position: 50% 0%;
      }
    }
  }

  &.bg-bottom {
    .bg-image-wrapper {
      .bg-image {
        background-position: 50% 100%;
      }
    }
  }

  &.bg-top-right {
    .bg-image-wrapper {
      .bg-image {
        background-position: 100% 0%;
      }
    }
  }

  &.bg-top-70 {
    .bg-image-wrapper {
      .bg-image {
        background-position: 50% 0%;
      }
    }
  }

  &.bg-bottom-70 {
    .bg-image-wrapper {
      .bg-image {
        background-position: 50% 70%;
      }
    }
  }

  &.bg-bottom-90 {
    .bg-image-wrapper {
      .bg-image {
        background-position: 50% 90%;
      }
    }
  }

  &.bg-bottom-left {
    .bg-image-wrapper {
      .bg-image {
        background-position: 0% 100%;
      }
    }
  }

  &.x-n30 {
    .bg-image-wrapper {
      .bg-image {
        transform: translateX(30%);
      }
    }
  }

  &.bg-fixed {
    .bg-image-wrapper {
      position: fixed;
      left: 0;
      right: 0;
      z-index: -1;
    }
  }

  &.bg-parallax {
    .bg-image-wrapper {
      .bg-image {
        background-attachment: fixed;
      }
    }
  }
}

// SM AND UP
@media only screen and (min-width: 576px) {
  .bg-image-component {
    &.bg-opacity-sm-100 {
      .bg-image-wrapper {
        &.loaded {
          .bg-image {
            &.bg-image-high {
              opacity: 1;
            }
          }
        }
      }
    }

    &.bg-sm-left {
      .bg-image-wrapper {
        .bg-image {
          background-position: 0% 0%;
        }
      }
    }

    &.x-sm-n30 {
      .bg-image-wrapper {
        .bg-image {
          transform: translateX(30%);
        }
      }
    }
  }
}

// MD AND UP
@media only screen and (min-width: 768px) {}

// LG AND UP
@media only screen and (min-width: 992px) {
  .bg-image-component {
    .bg-image-wrapper {
      &.bg-lg-left-30 {
        .bg-image {
          background-position: 30% 100%;
        }
      }
    }

    .bg-image-wrapper {
      &.bg-lg-30-100 {
        .bg-image {
          background-position: 30% 100%;
        }
      }
    }
  }
}

// XL AND UP
@media only screen and (min-width: 1200px) {
  .bg-image-component {
    .bg-image-wrapper {
      &.bg-xl-left-10 {
        .bg-image {
          background-position: 0% 0%;
        }
      }
    }

    .bg-image-wrapper {
      &.bg-xl-10-50 {
        .bg-image {
          background-position: 50% 10%;
        }
      }
    }
  }
}

// XXL AND UP
@media only screen and (min-width: 1367px) {
  .bg-image-component {
    .bg-image-wrapper {
      &.bg-xxl-50-50 {
        .bg-image {
          background-position: 50% 50%;
        }
      }
    }
  }
}
</style>